
import { Options, Vue } from 'vue-class-component';
import AppButton from '@/components/Common/AppButton.vue';
import { CalendarIcon, ChevronDownIcon, RefreshIcon, SearchIcon, UploadIcon } from '@heroicons/vue/solid';
import { DatePicker } from 'v-calendar';
import { useApolloClient, useMutation } from '@vue/apollo-composable';
import { useToast } from 'vue-toastification';
import { format, isSameMonth } from 'date-fns';
import {
  GetAnnotatorImportDataDocument,
  ImportAnnotatorTimeRecordsDocument,
  AnnotatorRecord,
  AnnotatorImportError,
} from '@/graphql/generated-operations';
import { processResponseErrors } from '@/helpers/errors';

interface SourceForm {
  task: string;
}

interface Preview {
  honeydew: AnnotatorRecord[];
  cantaloupe: AnnotatorRecord[];
}

@Options({
  components: {
    AppButton,
    DatePicker,
    CalendarIcon, ChevronDownIcon, RefreshIcon, SearchIcon, UploadIcon,
  },
})
export default class ImportAnnotators extends Vue {

  date: { start: Date | null, end: Date | null } = {
    start: null,
    end: null,
  };

  workingHoursPerDay = 8;

  honeydew: SourceForm = { task: '' };
  cantaloupe: SourceForm = { task: '' };

  fetchInProgress = false;
  importInProgress = false;

  preview: Preview | null = null;
  validationErrors: AnnotatorImportError[] = [];
  honeydewExpanded = false;
  cantaloupeExpanded = false;

  validateForm(): boolean {
    if ( !this.date.start || !this.date.end ) {
      useToast().error('Please select a date range.');
      return false;
    }
    if ( this.date.start > this.date.end ) {
      useToast().error('"From" date must not be after "To" date.');
      return false;
    }
    if ( !isSameMonth(this.date.start, this.date.end) ) {
      useToast().error('Date range must be within a single month.');
      return false;
    }
    if ( !Number.isFinite(this.workingHoursPerDay) || this.workingHoursPerDay < 1 || this.workingHoursPerDay > 24 ) {
      useToast().error('Working Hours per Day must be a number between 1 and 24.');
      return false;
    }
    return true;
  }

  async loadPreview() {
    if ( !this.validateForm() ) return;
    this.fetchInProgress = true;
    this.validationErrors = [];

    try {
      const query = await useApolloClient().client.query({
        query: GetAnnotatorImportDataDocument,
        variables: {
          filter: {
            dateFrom: format(this.date.start!, 'y-MM-dd'),
            dateTo: format(this.date.end!, 'y-MM-dd'),
          },
        },
      });

      const data = query.data.getAnnotatorImportData;

      if ( data.errors.length > 0 ) {
        this.validationErrors = data.errors;
        return;
      }

      this.preview = {
        honeydew: data.honeydew,
        cantaloupe: data.cantaloupe,
      };
      this.honeydewExpanded = false;
      this.cantaloupeExpanded = false;
    }
    catch ( error: any ) {
      useToast().error(`Failed to load preview: ${error?.message ?? error}`);
    }
    finally {
      this.fetchInProgress = false;
    }
  }

  cancelPreview() {
    this.preview = null;
  }

  buildSourcePayload(records: AnnotatorRecord[], task: string) {
    return {
      task: task.length > 0 ? task : null,
      records: records.map(r => ({
        externalId: r.externalId,
        projectExternalId: r.projectExternalId,
        hours: r.hours,
      })),
    };
  }

  async confirmImport() {
    if ( !this.preview ) return;
    this.importInProgress = true;

    try {
      const totalCount = this.preview.honeydew.length + this.preview.cantaloupe.length;
      if ( totalCount === 0 ) {
        useToast().error('No records to import.');
        this.importInProgress = false;
        return;
      }

      const { mutate: importAnnotator } = useMutation(ImportAnnotatorTimeRecordsDocument);
      const result = await importAnnotator({
        payload: {
          dateFrom: format(this.date.start!, 'y-MM-dd'),
          dateTo: format(this.date.end!, 'y-MM-dd'),
          workingHoursPerDay: this.workingHoursPerDay,
          honeydew: this.buildSourcePayload(this.preview.honeydew, this.honeydew.task),
          cantaloupe: this.buildSourcePayload(this.preview.cantaloupe, this.cantaloupe.task),
        },
      });

      const isSuccess = processResponseErrors(result?.data?.importAnnotatorTimeRecords);
      if ( !isSuccess ) {
        this.importInProgress = false;
        return;
      }

      useToast().success(`Imported ${totalCount} record(s) successfully.`);
      this.preview = null;
    }
    catch ( error: any ) {
      useToast().error(`Import failed: ${error?.message ?? error}`);
    }
    finally {
      this.importInProgress = false;
    }
  }

}
